import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, FileText, Star, Bookmark, MessageCircle, Users, 
  TrendingUp, Calendar, Loader2, ArrowUp, ArrowDown 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MemberAnalyticsDashboard({ className = '' }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (token) {
      loadAnalytics();
    }
  }, []);

  const loadAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/analytics/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setAnalytics(await res.json());
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-[#0d1525] rounded-xl border border-white/10 p-6 ${className}`}>
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const stats = [
    { 
      label: 'Profile Views', 
      value: analytics.profile_views, 
      icon: Eye, 
      color: 'cyan',
      change: '+12%' 
    },
    { 
      label: 'Content Views', 
      value: analytics.content_views, 
      icon: FileText, 
      color: 'teal',
      change: '+8%' 
    },
    { 
      label: 'Reviews Written', 
      value: analytics.total_reviews, 
      icon: Star, 
      color: 'amber',
      change: '+2' 
    },
    { 
      label: 'Bookmarks', 
      value: analytics.total_bookmarks, 
      icon: Bookmark, 
      color: 'purple',
      change: '+5' 
    },
    { 
      label: 'Messages', 
      value: analytics.messages_received + analytics.messages_sent, 
      icon: MessageCircle, 
      color: 'pink',
      change: '+15%' 
    },
    { 
      label: 'Followers', 
      value: analytics.follower_count, 
      icon: Users, 
      color: 'emerald',
      change: '+3' 
    },
  ];

  const colorClasses = {
    cyan: 'from-cyan-500 to-cyan-600',
    teal: 'from-teal-500 to-teal-600',
    amber: 'from-amber-500 to-amber-600',
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600',
    emerald: 'from-emerald-500 to-emerald-600',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-[#0d1525] rounded-xl border border-white/10 overflow-hidden ${className}`}
      data-testid="member-analytics-dashboard"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/10 bg-gradient-to-r from-cyan-500/10 to-teal-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Profile Analytics</h3>
            <p className="text-sm text-gray-400">Track your community engagement</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClasses[stat.color]} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                    <ArrowUp className="w-3 h-3" />
                    {stat.change}
                  </span>
                </div>
                <p className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Weekly Chart */}
        {analytics.views_this_week && analytics.views_this_week.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Views This Week
            </h4>
            <div className="flex items-end justify-between gap-2 h-20">
              {analytics.views_this_week.reverse().map((day, index) => (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className="w-full bg-gradient-to-t from-cyan-500/20 to-cyan-500/60 rounded-t"
                    style={{ 
                      height: `${Math.max(4, (day.views / Math.max(...analytics.views_this_week.map(d => d.views || 1))) * 100)}%`,
                      minHeight: '4px'
                    }}
                  />
                  <span className="text-xs text-gray-500">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Content */}
        {analytics.top_content && analytics.top_content.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Top Performing Content
            </h4>
            <div className="space-y-3">
              {analytics.top_content.map((content, index) => (
                <div key={content.id} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-xs text-gray-400 font-medium">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{content.title}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {content.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3" /> {content.likes}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default MemberAnalyticsDashboard;
